import { X } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Chip input for the list-of-string specification fields (certifications,
 * features, secondary materials). Enter or comma commits, backspace on an
 * empty input removes the last chip.
 */
export function SpecTagsInput({
	value,
	onChange,
	placeholder,
	disabled,
}: {
	value: string[];
	onChange: (next: string[]) => void;
	placeholder?: string;
	disabled?: boolean;
}) {
	const [draft, setDraft] = useState('');

	const commit = (raw: string) => {
		const tag = raw.trim();
		if (!tag || value.some((v) => v.toLowerCase() === tag.toLowerCase())) {
			setDraft('');
			return;
		}
		onChange([...value, tag]);
		setDraft('');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' || e.key === ',') {
			e.preventDefault();
			commit(draft);
			return;
		}
		if (e.key === 'Backspace' && draft === '' && value.length > 0) {
			onChange(value.slice(0, -1));
		}
	};

	return (
		<div
			className={cn(
				'flex min-h-8 flex-wrap items-center gap-1.5 rounded-lg border border-line-2 px-1.5 py-1 transition-colors focus-within:border-ring',
				disabled && 'pointer-events-none opacity-50'
			)}
		>
			{value.map((tag) => (
				<span
					key={tag}
					className="flex items-center gap-1 rounded-md bg-surface-fill py-0.5 pr-1 pl-2 text-xs text-text-1"
				>
					{tag}
					<button
						type="button"
						onClick={() => onChange(value.filter((v) => v !== tag))}
						className="flex h-4 w-4 cursor-pointer items-center justify-center rounded text-text-4 transition-colors hover:bg-surface-input hover:text-text-1"
					>
						<X size={11} />
					</button>
				</span>
			))}
			<input
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={handleKeyDown}
				onBlur={() => commit(draft)}
				disabled={disabled}
				placeholder={value.length === 0 ? placeholder : undefined}
				className="min-w-[8ch] flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground"
			/>
		</div>
	);
}
